"use client";

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, ExternalLink, Download } from "lucide-react";
import { getResourceBySlug, type ResourceItem, type ResourceType, getTags, type Tag } from "@/lib/resources";
import BackButton from "@/components/ui/back-button";

export default function ResourceDetail({ type, backTo, label }: { type: ResourceType; backTo: string; label: string }) {
  const { slug } = useParams();
  const [item, setItem] = useState<ResourceItem | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const [it, allTags] = await Promise.all([
          getResourceBySlug(type, String(slug || "")),
          getTags().catch(() => [] as Tag[]),
        ]);
        if (it && it.isVisible === false) {
          setItem(null);
          setErr("Not found");
        } else {
          setItem(it);
          if (!it) setErr("Not found");
        }
        setTags(allTags);
      } catch (e: any) {
        setErr(e?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, type]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white border border-border rounded-2xl p-8">
          <h1 className="text-2xl font-black text-text-primary mb-2">{label}</h1>
          <p className="text-text-secondary">{err || "Not found"}</p>
          <Link to={backTo} className="inline-flex items-center gap-2 mt-6 text-primary font-black hover:underline">
            <ArrowLeft size={16} /> Back
          </Link>
        </div>
      </div>
    );
  }

  const tagNames =
    (item.tagIds || [])
      .map((id) => tags.find((t) => t.id === id)?.name)
      .filter(Boolean) as string[];

  return (
    <div className="min-h-screen bg-bg-secondary/30">
      <section className="bg-white border-b border-border pt-24 pb-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackButton className="mb-6" />
          <h1 className="mt-4 text-3xl sm:text-4xl font-black text-text-primary">{item.title}</h1>
          <p className="mt-3 text-text-secondary">{item.description}</p>

          {tagNames.length > 0 ? (
            <div className="mt-5 flex flex-wrap gap-2">
              {tagNames.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-black">
                  #{t}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {(item.imageUrls || []).length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(item.imageUrls || []).map((u) => (
              <div key={u} className="bg-white border border-border rounded-2xl overflow-hidden">
                <img src={u} alt={item.title} className="w-full h-56 object-cover" />
              </div>
            ))}
          </div>
        ) : null}

        {item.content ? (
          <div className="bg-white border border-border rounded-2xl p-6">
            <div className="prose max-w-none text-text-secondary">{item.content}</div>
          </div>
        ) : null}

        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex flex-wrap gap-3">
            {item.link ? (
              <a
                href={item.link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-black hover:bg-primary-dark transition-smooth"
              >
                <ExternalLink size={16} /> Open Link
              </a>
            ) : null}
            {(item.documentUrls || []).map((u) => (
              <a
                key={u}
                href={u}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border font-black hover:bg-bg-secondary transition-smooth"
              >
                <Download size={16} /> Download
              </a>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

